import {Box, SelectChangeEvent, Typography} from '@mui/material';
import AurButton from '../../../common/aur-button/AurButton';
import {ReactComponent as PlusIcon} from '../../../../assests/svg/plus-icon.svg';
import {useCallback, useMemo, useRef, useState, FC} from 'react';
import {AurSelect} from '../../../common/aur-select/AurSelect';
import {t} from 'i18next';
import TagList from './TagList';
import {AurLoadingButton} from '../../../common/aur-loading-button/AurLoadingButton';
import {useGetTagNames} from '../../../../hooks/eca-tagging/useGetTagNames';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {useUpdateTagNames} from '../../../../hooks/eca-tagging/useUpdateTagNames';
import {ITaggingPannelProps} from './TaggingPanel';
import {IFieldModal} from '../../../pages/field-management/custom-field/constant';
import {ITag} from '../../../pages/field-management/custom-field/DraggableTag';

interface SelectFieldAndApplyTagProps extends ITaggingPannelProps {
  tagsFieldList: IFieldModal[];
  onApplyTagField: (newTagField: IFieldModal, selectedTags: ITag[]) => void;
}
const SelectFieldAndApplyTag: FC<SelectFieldAndApplyTagProps> = ({
  tagsFieldList,
  onApplyTagField,
  activeDocumentId,
  searchId,
  indexName,
}) => {
  const [selectedTags, setSelectedTags] = useState<ITag[]>([]);
  const [hideFields, setHideFields] = useState<boolean>(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedTagField, setSelectedTagField] = useState<IFieldModal>();
  const [selectedFieldId, setSelectedFieldId] = useState<string>();

  const {updateTagName, isLoading: isLoadingOnApply} = useUpdateTagNames(true);

  const {
    getAllTagNames,
    isFailedToLoadContent: isFailedToLoadTagNameContent,
    isLoading: isTagsLoading,
  } = useGetTagNames();

  const fetchAllTagName = useCallback(
    async (selectedValue: string) => {
      const result = await getAllTagNames(selectedValue);
      setSelectedTagField(result);
      setSelectedTags([]);
    },
    [getAllTagNames]
  );
  const toggleView = useCallback(() => {
    setIsAddingNew((prev) => !prev);
  }, []);

  const applyScopeKey = useMemo(
    () => JSON.stringify([activeDocumentId, searchId, indexName]),
    [activeDocumentId, indexName, searchId]
  );
  const currentApplyScopeKeyRef = useRef(applyScopeKey);
  currentApplyScopeKeyRef.current = applyScopeKey;

  const resetAndToggleView = useCallback(() => {
    setSelectedTagField(undefined);
    setSelectedFieldId(undefined);
    setSelectedTags([]);
    toggleView();
  }, [toggleView]);

  const updateSelectedTags = useCallback((tags: ITag[]) => {
    setSelectedTags(tags);
  }, []);

  const onApply = useCallback(async () => {
    if (!isLoadingOnApply && selectedTagField) {
      await updateTagName(
        'tag',
        indexName,
        {
          allDocuments: false,
          documentIds: [activeDocumentId],
          searchId: null,
          tagFieldId: selectedFieldId,
          tagIds: selectedTags.map((tags) => tags.tagValueId),
        },
        () => {
          onApplyTagField(selectedTagField, selectedTags);
          if (currentApplyScopeKeyRef.current === applyScopeKey) {
            resetAndToggleView();
          }
        }
      );
    }
  }, [
    isLoadingOnApply,
    selectedTagField,
    updateTagName,
    indexName,
    activeDocumentId,
    searchId,
    selectedFieldId,
    selectedTags,
    onApplyTagField,
    applyScopeKey,
    resetAndToggleView,
  ]);

  const tagFieldOptions = useMemo(() => {
    return tagsFieldList.map((tagField) => ({
      key: tagField.fieldId.toString(),
      value: tagField.fieldId.toString(),
      label: tagField.fieldLabel,
    }));
  }, [tagsFieldList]);

  const handleTagFieldChange = useCallback(
    (event: SelectChangeEvent) => {
      const selectedValue = event.target.value;
      setSelectedFieldId(selectedValue);
      fetchAllTagName(selectedValue);
    },
    [fetchAllTagName]
  );

  const addNewTagToList = useCallback((tag: ITag) => {
    setSelectedTagField((prev) => {
      if (prev) {
        return {
          ...prev,
          tagValues: [tag, ...prev.tagValues],
        };
      }
      return prev;
    });
    setSelectedTags((prev) => [...prev, tag]);
  }, []);

  const getTagListView = () => {
    if (isFailedToLoadTagNameContent) {
      return (
        <Box className="content-loading-error">
          <AurDataGridErrorOverlay
            onRefresh={() => fetchAllTagName(selectedFieldId ?? '')}
            enableParentWrapper={false}
          />
        </Box>
      );
    } else if (selectedTagField) {
      return (
        <TagList
          key={selectedTagField?.fieldId}
          tagField={selectedTagField}
          setHidefields={setHideFields}
          navigationScreen={'selectFieldAndApplyTag'}
          hideHeader
          index={0}
          onSelectionUpdate={updateSelectedTags}
          addNewTagToList={addNewTagToList}
        ></TagList>
      );
    }
    return null;
  };

  if (tagsFieldList.length <= 0) {
    return null;
  }

  return (
    <Box
      className={`select-field-and-apply-tag  ${isAddingNew ? 'add-field-pannel' : ''} ${hideFields ? 'add-tag-panel' : ''}`}
    >
      {!isAddingNew ? (
        <AurButton
          type="continue"
          classNames="customButton"
          title={t('eca.documentPreviewer.taggingPanel.selectFieldButton')}
          label={t('eca.documentPreviewer.taggingPanel.selectFieldButton')}
          size="Medium"
          startIcon={<PlusIcon aria-hidden="true" />}
          onClick={toggleView}
          dataTestId="select-field"
        />
      ) : (
        <>
          {!hideFields ? (
            <Box className="list-header-container ">
              <Box className="header">
                <Typography className={`typo-text subhead3_size`}>
                  {t('eca.documentPreviewer.taggingPanel.selectField')}
                </Typography>
                <Box className="button-container">
                  <AurButton
                    type="secondary"
                    size="Small"
                    title={t('buttons.cancel')}
                    label={t('buttons.cancel')}
                    onClick={resetAndToggleView}
                    classNames="small-border-radius-button tag-button cancel-button"
                    dataTestId="cancel-button"
                  />
                  <AurLoadingButton
                    label={t('buttons.apply')}
                    loading={isLoadingOnApply}
                    onClick={onApply}
                    size="Small"
                    buttonType="submit"
                    classNames={`small-border-radius-button ${isLoadingOnApply && 'apply-button-loading'}`}
                    disabled={selectedTags.length === 0 && !isLoadingOnApply}
                    dataTestId="apply-button"
                  />
                </Box>
              </Box>
            </Box>
          ) : (
            ''
          )}
          <Box
            className={`tag-field-container ${selectedFieldId && 'selected-tag-field-container'}`}
          >
            {!hideFields ? (
              <AurSelect
                dataTestId="aur-select-tag-field-dropdown"
                label={t('ecaDocList.bulkTagging.selectTagField')}
                optionList={tagFieldOptions}
                field={{
                  value: selectedFieldId,
                  onChange: handleTagFieldChange,
                }}
                fieldState={{}}
                style={{width: '100%', paddingRight: '16px'}}
              />
            ) : (
              ''
            )}
            {selectedFieldId &&
              (isTagsLoading ? (
                <Box className="tag-loading-container">
                  <AurLoadingIndicator show={isTagsLoading} />
                </Box>
              ) : (
                getTagListView()
              ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default SelectFieldAndApplyTag;
